import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const cls = "rounded-lg border border-navy/15 bg-white px-2 py-1.5 text-xs outline-none focus:border-teal";

function QueueRow({ q, onDone }) {
    const [f, setF] = useState({ booking_ref: q.booking_ref || "", patient_id: "", note: "" });
    const act = async (action) => {
        try { await api.post(`/admin/healthcare/review-queue/${q.id}/resolve`, { action, ...f }); toast.success(action === "reject" ? "Rejected" : "Attached. Verify it from the booking to release."); onDone(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };
    return (
        <div data-testid={`hc-queue-${q.id}`} className="rounded-2xl border border-amber-200 bg-white p-3 text-xs">
            <p className="font-bold text-navy">{q.meta?.report_name || "Report"} · lab ref {q.provider_report_ref}</p>
            <p className="text-navy/60">Uploaded against {q.booking_ref || q.booking_id} with patient ID <b>{q.given_patient_id}</b>, which didn't match · {q.by} · {(q.at || "").slice(0, 16).replace("T", " ")}</p>
            {!q.doc_path && q.meta === undefined && <p className="text-amber-700">Logged before files were kept. Ask the lab to resend and upload again.</p>}
            <div className="mt-2 flex flex-wrap gap-2">
                <input data-testid={`hc-queue-booking-${q.id}`} aria-label="Booking ID" placeholder="Booking ID (HC-…)" value={f.booking_ref} onChange={(e) => setF({ ...f, booking_ref: e.target.value })} className={cls} />
                <input data-testid={`hc-queue-patient-${q.id}`} aria-label="Patient ID" placeholder="Patient ID printed on report" value={f.patient_id} onChange={(e) => setF({ ...f, patient_id: e.target.value })} className={cls} />
                <button data-testid={`hc-queue-attach-${q.id}`} onClick={() => act("attach")} className="rounded-full bg-teal px-3 py-1 font-bold text-white">Attach (both must match)</button>
                <input data-testid={`hc-queue-note-${q.id}`} aria-label="Reason" placeholder="Reason to reject" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={cls} />
                <button data-testid={`hc-queue-reject-${q.id}`} onClick={() => act("reject")} className="rounded-full border border-red-200 px-3 py-1 font-bold text-red-600">Reject</button>
            </div>
        </div>
    );
}

export function HcReviewQueue() {
    const [rows, setRows] = useState(null);
    const load = useCallback(() => { api.get("/admin/healthcare/review-queue").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
    useEffect(() => { load(); }, [load]);
    if (rows === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;
    return (
        <div data-testid="hc-review-queue" className="space-y-2">
            <p className="text-xs text-navy/55">Reports whose patient ID didn't match the booking are held here and never shown to customers. Attach one only when the booking ID and patient ID on the lab report both match a booking. It then waits for verification like any upload.</p>
            {rows.map((q) => <QueueRow key={q.id} q={q} onDone={load} />)}
            {!rows.length && <p className="py-10 text-center text-sm text-navy/40">Nothing waiting for review.</p>}
        </div>
    );
}
